import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import {
  ObservableLevelsEnum,
  PartCategory,
  PartName,
} from '../../../../data/enums';
import { CompleteButton } from '../../../../components/atoms/Button';
import { useEffect, useMemo, useState } from 'react';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import { ActionStatusBar } from '../../../../components/molecules/ActionStatusBar';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import CheckboxWithLabel from '../../../../components/atoms/Checkbox';
import { Label } from '../../../../components/atoms/Label';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { VisualCondition } from '../../../../components/atoms/RadioButtonGroupField';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { flattenObjectStrings } from '../../../../utils/flattenObjectString';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { useAssetEquipmentPartCached } from '../../../../hooks/useAssetEquipmentPartCached';
import { MdHelpers } from '../../../../helpers/mdHelpers';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';

const { Undercarriage } = ObservableItemIdentifier.Identifiers;

export const IdlerRearScreen = () => {
  const { externalId, sideName } = useParams();
  const navigate = useNavigate();

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const {
    equipmentPart,
    error: errorEquipmentPart,
    asset,
  } = useAssetEquipmentPartCached({
    externalId,
    asset: inspection?.asset,
    sideName,
    partCategory: PartCategory.idler,
    partName: PartName.rearIdler,
  });
  const [assetType, setAssetType] = useState<'ERS' | 'HEX'>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null); // reset error
    async function getEquipment() {
      try {
        if (asset) {
          setAssetType(asset.model.assetType as 'ERS' | 'HEX');
        }
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    }

    getEquipment();
  }, [asset, equipmentPart, errorEquipmentPart]);

  const listWearIndicators = useMemo(
    () =>
      [
        {
          label: 'Collapsed',
          path: Undercarriage.Idler.WearIndicators.Collapsed,
        },
        {
          label: 'Excessive Axle Bore Clearance',
          path: Undercarriage.Idler.WearIndicators.ExcessAxleBoreClearance,
        },
        {
          label: 'Thrust Washer Damaged Or Missing',
          path: Undercarriage.Idler.WearIndicators.ThrustWasherDamagedOrMissing,
        },
        {
          label: 'Loose Or Missing Retaining Hardware',
          path: Undercarriage.Idler.WearIndicators
            .LooseOrMissingRetainingHardware,
        },
        {
          label: 'Roller Path Cracking/Delamination/Plastic Deformation',
          path: Undercarriage.Idler.WearIndicators
            .RollerPathCrackingDelaminationPlasticDeformation,
        },
        {
          path: Undercarriage.Idler.WearIndicators.SevereCracking,
          label: 'Severe Cracking',
        },
      ].map((el) => ({
        label: el.label,
        path: el.path,
      })),
    [assetType],
  );

  if (!equipmentPart || !externalId || !undercarriageCategory) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <ObservationDraftScope inspectionId={externalId}>
      <div className="-mt-4">
        <div className="sticky -top-4 z-10 bg-white pt-4">
          <ScreenHeading
            eyebrow="Idler"
            title="Rear"
            tag={sideName?.split('-')[0]}
            partNumber={equipmentPart.part.partNumber}
            tooltipKey={'undercarriage-idler-rear-header'}
          />

          <div className="-mx-4 mt-2 bg-white">
            <ActionStatusBar
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entities={[
                {
                  entityId: equipmentPart.id,
                  entityExternalId: equipmentPart.externalId,
                },
              ]}
              showPercentage={false}
              paths={[
                ...flattenObjectStrings(Undercarriage.Idler),
                Undercarriage.Idler.Rear.VisualCondition,
                ...listWearIndicators.map((e) => e.path),
              ]}
              pathsLike={[]}
            />
          </div>
        </div>

        <ScreenObservationLayout>
          <VisualCondition
            tooltipKey={'undercarriage-idler-rear-visual-condition'}
            path={Undercarriage.Idler.Rear.VisualCondition}
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
          />

          <Label titleType="fieldTitle">Wear Indicators</Label>
          {listWearIndicators.map((e) => (
            <CheckboxWithLabel
              key={e.path}
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entityId={equipmentPart.id}
              entityExternalId={equipmentPart.externalId}
              infoTooltipKey={`undercarriage-idler-rear-wearindicators-${MdHelpers.normaliseTooltipKeyComponent(e.label)}`}
              {...e}
            />
          ))}
          <PhotoSelector
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            title="Photos"
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Photo
            }
          />
          <RichTextField
            label="Comments"
            className="mt-4"
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Comments
            }
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
          />

          <CompleteButton onClick={() => navigate(-1)} />
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
